import { Icon } from 'Components/shared/Icon';
import React, { useEffect } from 'react';
import { formatUserInitials } from 'Utils/formatUserInitials';
import { Avatar, AvatarFallback, AvatarImage } from '~/lib/ui/avatar';
import { CallBannerAvatarProps } from './index.types';

/**
 * CallBannerAvatar displays the caller's avatar with intelligent fallback logic.
 *
 * Display priority:
 * 1. Avatar image (if avatarUrl exists AND not a ring group)
 * 2. User initials (if displayNameForAvatar exists)
 * 3. Icon (phone-filled or video-filled)
 *
 * @remarks
 * Ring groups always show initials (never avatar image), even if avatarUrl exists.
 * Uses a longer delay (600ms) before showing fallback to prevent visual flashing.
 * Preloads avatar images for faster display.
 */
export const CallBannerAvatar = ({
  avatarUrl,
  displayNameForAvatar,
  isVideo,
  callerLabel,
}: CallBannerAvatarProps) => {
  const showInitials = Boolean(displayNameForAvatar);
  const iconName = isVideo ? 'video-filled' : 'call-filled';

  // Preload avatar image for faster display
  useEffect(() => {
    if (avatarUrl) {
      const img = new Image();
      img.src = avatarUrl;
    }
  }, [avatarUrl]);

  // If no avatar and no initials, show icon directly
  if (!avatarUrl && !showInitials) {
    return <Icon icon={iconName} className="text-foreground w-10 h-10" />;
  }

  return (
    <Avatar className="size-10">
      {avatarUrl && (
        <AvatarImage
          src={avatarUrl}
          alt={callerLabel}
          className="object-cover"
        />
      )}
      <AvatarFallback
        delayMs={avatarUrl ? 600 : 0}
        className={
          showInitials
            ? 'bg-foreground text-background text-[12px] font-normal'
            : 'bg-transparent'
        }
      >
        {showInitials ? (
          formatUserInitials(displayNameForAvatar)
        ) : (
          <Icon icon={iconName} className="text-foreground w-10 h-10" />
        )}
      </AvatarFallback>
    </Avatar>
  );
};
